import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader } from './ui/Card';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { CardListToolbar } from './ui/CardListToolbar';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Modal } from './ui/Modal';
import { DetailSection, DetailField } from './ui/DetailSection';
import { SearchableSelect } from './ui/SearchableSelect';
import { Textarea } from './ui/Textarea';
import { FilterTabs } from './ui/FilterTabs';
import { useModuleAccess } from '../permissions/useModuleAccess';
import { useCommercialAccess } from '../permissions/useCommercialAccess';
import { ModuleAccessBanner } from './ui/ModuleAccessBanner';
import { AuthenticatedImage } from './ui/AuthenticatedImage';
import { API_BASE_URL } from '../api/mediaUrl';
import { messageFromFetchResponse } from '../api/parseFetchError';
import { validateImageUploadSize } from '../config/uploadConfig';
import { takeDesignReturnOrderId } from './orders/orderReturnContext';
import { ordersOpenOrderTarget } from './orders/orderListFilterUtils';
import { OrderDetail } from './orders/OrderDetail';
import {
  parseDesignsOverlay,
  designsNavigateTarget,
} from './designs/designsRouteUtils';
import {
  applyDesignListFilters,
  countActiveDesignFilters,
  DEFAULT_DESIGN_LIST_FILTERS,
} from './designs/designListFilterUtils';
import { DesignListFiltersPanel } from './designs/DesignListFiltersPanel';

const buildEmptyFeederRow = () => ({ id: Date.now() + Math.random(), feederId: '' });

/** Add picks — free text (e.g. 27/67, 120). */
function parseAddPicksField(fieldKey, raw, errors) {
  const s = String(raw ?? '').trim();
  if (s === '') {
    errors[fieldKey] = 'Required';
    return null;
  }
  if (s.length > 50) {
    errors[fieldKey] = 'Max 50 characters';
    return null;
  }
  if (!/[\d]/.test(s)) {
    errors[fieldKey] = 'Enter a valid pick value (e.g. 27/67)';
    return null;
  }
  return s;
}

/** Whole non‑negative int for mandatory production counts (0+). */
function parseNonNegativeIntField(fieldKey, raw, errors) {
  const s = String(raw ?? '').trim();
  if (s === '') {
    errors[fieldKey] = 'Required';
    return null;
  }
  if (!/^\d+$/.test(s)) {
    errors[fieldKey] = 'Whole number (0 or more) only';
    return null;
  }
  const n = Number.parseInt(s, 10);
  if (n < 0 || n > 2147483647) {
    errors[fieldKey] = 'Out of range';
    return null;
  }
  return n;
}

/**
 * Non-negative decimal with up to {@code maxFractionDigits} fraction digits
 * (e.g. 118.57, 110.2). Returns the *string* representation so we avoid
 * floating-point drift when sending to the backend BigDecimal.
 */
function parseNonNegativeDecimalField(fieldKey, raw, errors, maxFractionDigits = 2) {
  const s = String(raw ?? '').trim();
  if (s === '') {
    errors[fieldKey] = 'Required';
    return null;
  }
  const pattern = new RegExp(`^\\d+(?:\\.\\d{1,${maxFractionDigits}})?$`);
  if (!pattern.test(s)) {
    errors[fieldKey] = `Number with up to ${maxFractionDigits} decimal places (e.g. 118.57)`;
    return null;
  }
  const n = Number.parseFloat(s);
  if (!Number.isFinite(n) || n < 0) {
    errors[fieldKey] = 'Out of range';
    return null;
  }
  return s;
}

/** Allow digits + a single dot, capped at {@code maxFractionDigits}. */
function sanitizeDecimalInput(raw, maxFractionDigits = 2) {
  let s = String(raw ?? '').replace(/[^\d.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  const dotAt = s.indexOf('.');
  if (dotAt !== -1) {
    const whole = s.slice(0, dotAt);
    const frac = s.slice(dotAt + 1, dotAt + 1 + maxFractionDigits);
    s = frac.length > 0 ? `${whole}.${frac}` : `${whole}.`;
  }
  return s;
}

function Designs() {
  const { canCreate, canEdit, canMutate } = useModuleAccess('DESIGNS');
  const { canCreate: canCreateOrder } = useModuleAccess('ORDERS');
  const canSeeCommercial = useCommercialAccess();
  const canOrderDesign = canCreateOrder && canSeeCommercial;
  const location = useLocation();
  const navigate = useNavigate();

  const overlay = useMemo(
    () => parseDesignsOverlay(location.search),
    [location.search]
  );

  const [items, setItems] = useState([]);
  const [designFilters, setDesignFilters] = useState(() => ({ ...DEFAULT_DESIGN_LIST_FILTERS }));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [designSection, setDesignSection] = useState('regular'); // regular | sample
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const [conceptOptions, setConceptOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [feederOptions, setFeederOptions] = useState([]);

  const [form, setForm] = useState({
    conceptId: '',
    categoryId: '',
    subCategoryId: '',
    sampleNo: '',
    addPicks: '',
    averagePicks: '',
    totalCards: '',
    readCount: '',
    note: '',
    sampleDesign: false,
    singleBeam: false,
    beamName: '',
    designImage: '',
    feeders: [buildEmptyFeederRow()],
  });

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) {
      h.Authorization = `Bearer ${token}`;
    }
    return h;
  }, []);

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchInsightOptions = async () => {
    try {
      const [conceptRes, categoryRes, subCategoryRes, feederRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/insights?type=CONCEPT`, { headers }),
        fetch(`${API_BASE_URL}/api/insights?type=CATEGORY`, { headers }),
        fetch(`${API_BASE_URL}/api/insights?type=SUB_CATEGORY`, { headers }),
        fetch(`${API_BASE_URL}/api/insights?type=FEEDER`, { headers }),
      ]);
      if (!conceptRes.ok || !categoryRes.ok || !subCategoryRes.ok || !feederRes.ok) {
        throw new Error('Unable to load dropdown options');
      }
      const [concepts, categories, subCategories, feeders] = await Promise.all([
        conceptRes.json(),
        categoryRes.json(),
        subCategoryRes.json(),
        feederRes.json(),
      ]);
      setConceptOptions(Array.isArray(concepts) ? concepts : []);
      setCategoryOptions(Array.isArray(categories) ? categories : []);
      setSubCategoryOptions(Array.isArray(subCategories) ? subCategories : []);
      setFeederOptions(Array.isArray(feeders) ? feeders : []);
    } catch (err) {
      setFormError(err?.message || 'Unable to load dropdown options');
    }
  };

  const fetchDesigns = async () => {
    setLoading(true);
    setPageError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/designs`, { headers });
      if (!response.ok) {
        throw new Error('Unable to load designs');
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data.slice() : [];
      list.sort((a, b) => {
        const ta = new Date(a?.createdAt || 0).getTime();
        const tb = new Date(b?.createdAt || 0).getTime();
        return tb - ta;
      });
      setItems(list);
    } catch (err) {
      setItems([]);
      setPageError(err?.message || 'Unable to load designs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesigns();
    if (canMutate) {
      fetchInsightOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canMutate]);

  const filteredItems = useMemo(() => {
    const base = applyDesignListFilters(items, designFilters);
    if (designSection === 'sample') {
      return base.filter((item) => item.sampleDesign === true);
    }
    return base.filter((item) => item.sampleDesign !== true);
  }, [items, designFilters, designSection]);

  const sectionCounts = useMemo(() => {
    const base = applyDesignListFilters(items, designFilters);
    let regular = 0;
    let sample = 0;
    for (const item of base) {
      if (item.sampleDesign === true) sample += 1;
      else regular += 1;
    }
    return { regular, sample };
  }, [items, designFilters]);

  const designFilterBadge = countActiveDesignFilters(designFilters);

  const previewDesign = useMemo(() => {
    if (!overlay.preview) return null;
    return items.find((item) => String(item.id) === String(overlay.preview)) || null;
  }, [overlay.preview, items]);

  const pushOverlay = useCallback(
    (next) => {
      navigate(designsNavigateTarget(next));
    },
    [navigate]
  );

  const openPreview = useCallback(
    (designId) => {
      if (String(overlay.preview) === String(designId) && !overlay.order) return;
      pushOverlay({ preview: designId });
    },
    [overlay.preview, overlay.order, pushOverlay]
  );

  const closePreview = useCallback(() => {
    navigate(designsNavigateTarget({}), { replace: true });
  }, [navigate]);

  const openOrder = useCallback(() => {
    if (!overlay.preview) return;
    pushOverlay({ preview: overlay.preview, order: 'new' });
  }, [overlay.preview, pushOverlay]);

  const closeOrder = useCallback(() => {
    navigate(designsNavigateTarget({ preview: overlay.preview }), { replace: true });
  }, [overlay.preview, navigate]);

  const handleDraftCreated = useCallback(
    (orderId) => {
      navigate(
        designsNavigateTarget({ preview: overlay.preview, order: String(orderId) }),
        { replace: true }
      );
    },
    [navigate, overlay.preview]
  );

  /** Preview id in URL but design missing — drop preview layer. */
  useEffect(() => {
    if (!overlay.preview || loading) return;
    if (!previewDesign) {
      navigate(designsNavigateTarget({ order: overlay.order }), { replace: true });
    }
  }, [overlay.preview, overlay.order, previewDesign, loading, navigate]);

  /** Unauthorized order overlay — fall back to design preview. */
  useEffect(() => {
    if (!overlay.order || canOrderDesign) return;
    navigate(designsNavigateTarget({ preview: overlay.preview }), { replace: true });
  }, [overlay.order, overlay.preview, canOrderDesign, navigate]);

  const showDesignPreview = Boolean(overlay.preview && previewDesign && !overlay.order);
  const showOrderOverlay = Boolean(overlay.order && canOrderDesign);

  const resetForm = () => {
    setForm({
      conceptId: '',
      categoryId: '',
      subCategoryId: '',
      sampleNo: '',
      addPicks: '',
      averagePicks: '',
      totalCards: '',
      readCount: '',
      note: '',
      sampleDesign: false,
      singleBeam: false,
      beamName: '',
      designImage: '',
      feeders: [buildEmptyFeederRow()],
    });
    setFieldErrors({});
    setFormError('');
  };

  const ensureInsightOptionsLoaded = () => {
    fetchInsightOptions();
  };

  const openCreateForm = () => {
    if (!canCreate) return;
    ensureInsightOptionsLoaded();
    setFormMode('create');
    setEditingItem(null);
    resetForm();
    setShowForm(true);
  };

  useEffect(() => {
    if (!location.state?.openCreateDesign) return;
    openCreateForm();
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.openCreateDesign, location.pathname, navigate]);

  const openEditForm = (item) => {
    if (!canEdit) return;
    ensureInsightOptionsLoaded();
    setFormMode('edit');
    setEditingItem(item);
    setForm({
      conceptId: item.conceptId ?? '',
      categoryId: item.categoryId ?? '',
      subCategoryId: item.subCategoryId ?? '',
      sampleNo: item.sampleNo || '',
      addPicks: item.addPicks != null ? String(item.addPicks) : '',
      averagePicks: item.averagePicks != null ? String(item.averagePicks) : '',
      totalCards: item.totalCards != null ? String(item.totalCards) : '',
      readCount: item.readCount != null ? String(item.readCount) : '',
      note: item.note || '',
      sampleDesign: item.sampleDesign === true,
      singleBeam: item.singleBeam === true,
      beamName: item.beamName || '',
      designImage: item.designImage || '',
      feeders: Array.isArray(item.feeders) && item.feeders.length > 0
        ? item.feeders.map((row) => ({ id: row.id || Date.now() + Math.random(), feederId: row.feederId ?? '' }))
        : [buildEmptyFeederRow()],
    });
    setFieldErrors({});
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormMode('create');
    setEditingItem(null);
    resetForm();
  };

  const downloadDesignPdf = async (item) => {
    if (!item?.id) {
      return;
    }
    setPageError('');
    setPdfLoadingId(item.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/designs/${item.id}/pdf`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `PDF download failed (${res.status})`);
      }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      const safe = String(item.sampleNo || item.id).replace(/[^\w.-]+/g, '_');
      a.download = safe.toLowerCase().endsWith('.pdf') ? safe : `design-${safe}.pdf`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      setPageError(err?.message || 'PDF download failed');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => {
      if (field !== 'categoryId') {
        return { ...prev, [field]: value };
      }
      // Changing category invalidates a sub category that belongs to another parent.
      const current = subCategoryOptions.find((opt) => String(opt.id) === String(prev.subCategoryId));
      const sameParent = current && String(current.parentId || '') === String(value || '');
      return {
        ...prev,
        categoryId: value,
        subCategoryId: sameParent ? prev.subCategoryId : '',
      };
    });
    setFieldErrors((prev) => {
      if (field === 'categoryId') {
        const { categoryId, subCategoryId, ...rest } = prev;
        return rest;
      }
      return prev[field] ? { ...prev, [field]: '' } : prev;
    });
  };

  const filteredSubCategoryOptions = useMemo(() => {
    if (!form.categoryId) return [];
    return subCategoryOptions.filter(
      (opt) => String(opt.parentId || '') === String(form.categoryId)
    );
  }, [subCategoryOptions, form.categoryId]);

  const updateFeederRow = (rowId, feederId) => {
    setForm((prev) => ({
      ...prev,
      feeders: prev.feeders.map((row) => (row.id === rowId ? { ...row, feederId } : row)),
    }));
    setFieldErrors((prev) => (prev.feeders ? { ...prev, feeders: '' } : prev));
  };

  const addFeederRow = () => {
    setForm((prev) => ({ ...prev, feeders: [...prev.feeders, buildEmptyFeederRow()] }));
  };

  const removeFeederRow = (rowId) => {
    setForm((prev) => {
      const nextRows = prev.feeders.filter((row) => row.id !== rowId);
      return { ...prev, feeders: nextRows.length > 0 ? nextRows : [buildEmptyFeederRow()] };
    });
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const sizeError = validateImageUploadSize(file);
    if (sizeError) {
      setFormError(sizeError);
      e.target.value = '';
      return;
    }
    setUploadingImage(true);
    setFormError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'designs');
      formData.append('nameHint', form.sampleNo || form.conceptName || 'design');
      const response = await fetch(`${API_BASE_URL}/api/files/upload`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to upload design image');
      }
      const uploaded = await response.json();
      setForm((prev) => ({ ...prev, designImage: uploaded.key || uploaded.url || '' }));
      setFieldErrors((prev) => (prev.designImage ? { ...prev, designImage: '' } : prev));
    } catch (err) {
      setFormError(err?.message || 'Unable to upload design image');
    } finally {
      setUploadingImage(false);
    }
  };

  const sampleNoConflict = useMemo(() => {
    const q = form.sampleNo.trim().toLowerCase();
    if (!q) return null;
    const editingId = editingItem?.id;
    return items.find(
      (row) => String(row.sampleNo || '').trim().toLowerCase() === q && row.id !== editingId
    ) || null;
  }, [form.sampleNo, items, editingItem]);

  const validateForm = () => {
    const nextErrors = {};
    if (!form.conceptId) nextErrors.conceptId = 'Select concept';
    if (!form.categoryId) nextErrors.categoryId = 'Select category';
    if (!form.subCategoryId) nextErrors.subCategoryId = 'Select sub category';
    if (!form.sampleNo.trim()) nextErrors.sampleNo = 'Enter sample number';
    else if (sampleNoConflict) nextErrors.sampleNo = 'This design number is already used';
    if (!form.designImage) nextErrors.designImage = 'Upload design';
    const invalidFeeder = form.feeders.some((row) => !row.feederId);
    if (invalidFeeder) nextErrors.feeders = 'Select feeder in each row';
    const addPicks = parseAddPicksField('addPicks', form.addPicks, nextErrors);
    const averagePicks = parseNonNegativeDecimalField('averagePicks', form.averagePicks, nextErrors);
    const totalCards = parseNonNegativeIntField('totalCards', form.totalCards, nextErrors);
    const readCount = parseNonNegativeIntField('readCount', form.readCount, nextErrors);
    if (form.singleBeam) {
      const bn = String(form.beamName || '').trim();
      if (!bn) nextErrors.beamName = 'Enter beam name';
      else if (bn.length > 200) nextErrors.beamName = 'Max 200 characters';
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return null;
    return { addPicks, averagePicks, totalCards, readCount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPageMessage('');
    setPageError('');
    setFormError('');
    const metrics = validateForm();
    if (!metrics) return;

    const concept = conceptOptions.find((opt) => String(opt.id) === form.conceptId);
    const category = categoryOptions.find((opt) => String(opt.id) === form.categoryId);
    const subCategory = subCategoryOptions.find((opt) => String(opt.id) === form.subCategoryId);

    if (!concept || !category || !subCategory) {
      setFormError('Please select valid concept, category and sub category');
      return;
    }

    const feederPayload = form.feeders.map((row) => {
      const feeder = feederOptions.find((opt) => String(opt.id) === row.feederId);
      return {
        feederId: row.feederId || null,
        feederName: feeder?.name || '',
      };
    });

    setSaving(true);
    try {
      const payload = {
        conceptId: form.conceptId,
        conceptName: concept.name,
        categoryId: form.categoryId,
        categoryName: category.name,
        subCategoryId: form.subCategoryId,
        subCategoryName: subCategory.name,
        sampleNo: form.sampleNo.trim(),
        designImage: form.designImage,
        addPicks: metrics.addPicks,
        averagePicks: metrics.averagePicks,
        totalCards: metrics.totalCards,
        readCount: metrics.readCount,
        note: String(form.note || '').trim(),
        sampleDesign: Boolean(form.sampleDesign),
        singleBeam: Boolean(form.singleBeam),
        beamName: form.singleBeam ? String(form.beamName || '').trim() : '',
        feeders: feederPayload,
      };
      const isCreate = formMode === 'create';
      const response = await fetch(
        isCreate ? `${API_BASE_URL}/api/designs` : `${API_BASE_URL}/api/designs/${editingItem.id}`,
        {
          method: isCreate ? 'POST' : 'PUT',
          headers,
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to save design');
      }
      const saved = await response.json();
      if (isCreate) {
        setItems((prev) => [saved, ...prev]);
      } else {
        setItems((prev) => prev.map((item) => (item.id === saved.id ? saved : item)));
      }
      const returnOrderId = isCreate ? takeDesignReturnOrderId() : null;
      if (returnOrderId) {
        navigate(ordersOpenOrderTarget(returnOrderId, saved.id));
        return;
      }
      setPageMessage(isCreate ? 'Design created successfully' : 'Design updated successfully');
      closeForm();
    } catch (err) {
      setFormError(err?.message || 'Unable to save design');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteItem) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/designs/${confirmDeleteItem.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) {
        throw new Error(await messageFromFetchResponse(response, 'Unable to delete design'));
      }
      setItems((prev) => prev.filter((item) => item.id !== confirmDeleteItem.id));
      setConfirmDeleteItem(null);
      setPageMessage('Design deleted successfully');
    } catch (err) {
      setConfirmDeleteItem(null);
      setPageError(err?.message || 'Unable to delete design');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <ModuleAccessBanner moduleKey="DESIGNS" />
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title="Designs"
            actions={
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setFiltersOpen((open) => !open)}
                  className="h-11 w-full shrink-0 sm:w-auto"
                >
                  <svg
                    className="mr-1.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                  Filters
                  {designFilterBadge > 0 ? (
                    <span className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 text-[10px] font-bold text-white">
                      {designFilterBadge}
                    </span>
                  ) : null}
                </Button>
                {canCreate && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={openCreateForm}
                    className="h-11 gap-2 whitespace-nowrap rounded-xl px-5"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Design</span>
                  </Button>
                )}
              </>
            }
          />
        </CardHeader>
        <CardBody>
          <AlertMessage message={pageMessage} type="success" onClose={() => setPageMessage('')} className="mb-4" />
          <AlertMessage message={pageError} type="error" onClose={() => setPageError('')} className="mb-4" />
          {filtersOpen ? (
            <DesignListFiltersPanel
              className="mb-4"
              value={designFilters}
              onApply={setDesignFilters}
              items={items}
            />
          ) : null}
          <div className="mb-4">
            <FilterTabs
              value={designSection}
              onChange={setDesignSection}
              tabs={[
                { id: 'regular', label: 'Designs', count: sectionCounts.regular },
                { id: 'sample', label: 'Sample designs', count: sectionCounts.sample },
              ]}
            />
          </div>
          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading designs...</div>
          ) : filteredItems.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              {items.length === 0
                ? 'No designs yet'
                : designSection === 'sample'
                  ? 'No sample designs match your filters'
                  : 'No designs match your filters'}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredItems.map((item) => {
                const dateStr = item.updatedAt
                  ? new Date(item.updatedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                  : '—';
                const designName = String(item.subCategoryName || item.conceptName || '—').trim() || '—';
                const iconBtn =
                  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200/80 bg-white shadow-sm transition-colors';
                return (
                  <div
                    key={item.id}
                    className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-md ring-1 ring-slate-900/[0.04]"
                  >
                    <div className="flex items-end justify-between gap-2 border-b border-slate-100 px-3 py-2">
                      <div className="min-w-0 flex-1 pr-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Design number</p>
                        <p className="truncate font-mono text-sm font-bold tabular-nums text-slate-900" title={item.sampleNo || ''}>
                          #{item.sampleNo || '—'}
                          {item.sampleDesign === true ? (
                            <span className="ml-1.5 align-middle rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                              Sample
                            </span>
                          ) : null}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {canEdit && (
                          <button
                            type="button"
                            className={`${iconBtn} text-sky-600 hover:bg-sky-50 hover:border-sky-200`}
                            title="Edit"
                            aria-label={`Edit design ${item.sampleNo || ''}`}
                            onClick={() => openEditForm(item)}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        <button
                          type="button"
                          className={`${iconBtn} text-amber-500 hover:bg-amber-50 hover:border-amber-200`}
                          title="Preview"
                          aria-label={`Preview design ${item.sampleNo || ''}`}
                          onClick={() => openPreview(item.id)}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {canEdit && (
                          <button
                            type="button"
                            className={`${iconBtn} text-rose-600 hover:bg-rose-50 hover:border-rose-200`}
                            title="Delete"
                            aria-label={`Delete design ${item.sampleNo || ''}`}
                            onClick={() => {
                              setPageError('');
                              setPageMessage('');
                              setConfirmDeleteItem(item);
                            }}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={pdfLoadingId === item.id}
                          className={`${iconBtn} text-red-600 hover:bg-red-50 hover:border-red-200 disabled:cursor-wait disabled:opacity-60`}
                          title="Download PDF"
                          aria-label={`Download PDF for design ${item.sampleNo || ''}`}
                          onClick={() => downloadDesignPdf(item)}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V7.5L14.5 2z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v4a2 2 0 002 2h4" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-6" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="m9 15 3 3 3-3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-3 p-3">
                      <div className="h-28 w-24 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                        {item.designImage ? (
                          <AuthenticatedImage
                            src={item.designImage}
                            alt={item.sampleNo ? `Design ${item.sampleNo}` : 'Design image'}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] leading-tight text-slate-500">
                            No image
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 space-y-2 text-[13px] leading-snug">
                        <div>
                          <p className="text-[11px] font-medium text-slate-500">Design name</p>
                          <p className="truncate text-sm font-bold uppercase tracking-wide text-slate-900" title={designName}>
                            {designName}
                          </p>
                        </div>
                        <p className="text-xs text-slate-600">
                          <span className="font-semibold text-slate-700">Concept =</span>{' '}
                          <span className="text-slate-800">{item.conceptName || '—'}</span>
                        </p>
                        <p className="text-xs text-slate-600">
                          <span className="font-semibold text-slate-700">Category =</span>{' '}
                          <span className="text-slate-800">{item.categoryName || '—'}</span>
                        </p>
                        <p className="text-xs text-slate-600">
                          <span className="font-semibold text-slate-700">Date</span>{' '}
                          <span className="tabular-nums text-slate-800">{dateStr}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={showForm}
        onClose={closeForm}
        title={formMode === 'create' ? 'Add design' : 'Edit design'}
        size="2xl"
        icon={(
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}
        bodyClassName="bg-slate-50/50"
        footer={(
          <>
            <Button type="button" variant="secondary" onClick={closeForm} disabled={saving}>Cancel</Button>
            <Button type="submit" form="design-form" variant="primary" loading={saving} disabled={saving}>
              {formMode === 'create' ? 'Save design' : 'Update design'}
            </Button>
          </>
        )}
      >
        {showForm && (
          <form id="design-form" onSubmit={handleSubmit} className="py-5 sm:py-6 space-y-5">
              <AlertMessage message={formError} type="error" onClose={() => setFormError('')} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SearchableSelect
                  label="Concept"
                  required
                  value={form.conceptId}
                  onChange={(id) => updateField('conceptId', id)}
                  placeholder="Type to find concept…"
                  emptyMessage="No concept starts with that text"
                  error={fieldErrors.conceptId || ''}
                  options={conceptOptions.map((item) => ({
                    value: String(item.id),
                    label: item.name || '',
                  }))}
                />
                <SearchableSelect
                  label="Category"
                  required
                  value={form.categoryId}
                  onChange={(id) => updateField('categoryId', id)}
                  placeholder="Type to find category…"
                  emptyMessage="No category starts with that text"
                  error={fieldErrors.categoryId || ''}
                  options={categoryOptions.map((item) => ({
                    value: String(item.id),
                    label: item.name || '',
                  }))}
                />
                <SearchableSelect
                  label="Sub Category"
                  required
                  value={form.subCategoryId}
                  onChange={(id) => updateField('subCategoryId', id)}
                  disabled={!form.categoryId}
                  placeholder={
                    form.categoryId
                      ? (filteredSubCategoryOptions.length === 0
                        ? 'No sub categories for this category'
                        : 'Type to find sub category…')
                      : 'Select category first'
                  }
                  emptyMessage="No sub category starts with that text"
                  error={fieldErrors.subCategoryId || ''}
                  options={filteredSubCategoryOptions.map((item) => ({
                    value: String(item.id),
                    label: item.name || '',
                  }))}
                />
                <Input
                  label="Design No. *"
                  placeholder="Unique design number"
                  value={form.sampleNo}
                  onChange={(e) => updateField('sampleNo', e.target.value)}
                  error={
                    fieldErrors.sampleNo
                    || (sampleNoConflict ? `Already used by "${sampleNoConflict.sampleNo}" (${sampleNoConflict.conceptName || '—'})` : '')
                  }
                  rightIcon={
                    form.sampleNo.trim()
                      ? sampleNoConflict
                        ? (
                          <svg className="w-4 h-4 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )
                        : (
                          <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        )
                      : undefined
                  }
                  required
                />
                <Input
                  label="Add picks *"
                  type="text"
                  autoComplete="off"
                  placeholder="e.g. 27/67"
                  value={form.addPicks}
                  onChange={(e) => updateField('addPicks', e.target.value.slice(0, 50))}
                  error={fieldErrors.addPicks}
                  required
                />
                <Input
                  label="Average picks *"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="e.g. 118.57"
                  value={form.averagePicks}
                  onChange={(e) => updateField('averagePicks', sanitizeDecimalInput(e.target.value, 2))}
                  error={fieldErrors.averagePicks}
                  required
                />
                <Input
                  label="Total cards *"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={form.totalCards}
                  onChange={(e) => updateField('totalCards', e.target.value.replace(/\D/g, ''))}
                  error={fieldErrors.totalCards}
                  required
                />
                <Input
                  label="Read count *"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={form.readCount}
                  onChange={(e) => updateField('readCount', e.target.value.replace(/\D/g, ''))}
                  error={fieldErrors.readCount}
                  required
                />
                <div className="md:col-span-2">
                  <Textarea
                    label="Note"
                    rows={3}
                    value={form.note}
                    onChange={(e) => updateField('note', e.target.value.slice(0, 2000))}
                    placeholder="Optional description for this design (shown on preview)"
                    hint={form.note ? `${form.note.length}/2000` : undefined}
                  />
                </div>
                <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={form.sampleDesign}
                      onChange={(e) => updateField('sampleDesign', e.target.checked)}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary-700 focus:ring-primary-600"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-slate-800">Sample design</span>
                      <span className="mt-0.5 block text-xs text-slate-600">
                        Orders using this design do not require party, broker, transport or price.
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 border-t border-slate-200/80 pt-3">
                    <input
                      type="checkbox"
                      checked={form.singleBeam}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => ({
                          ...prev,
                          singleBeam: checked,
                          beamName: checked ? prev.beamName : '',
                        }));
                        setFieldErrors((prev) => ({ ...prev, beamName: '' }));
                      }}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary-700 focus:ring-primary-600"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-slate-800">Single beam</span>
                      <span className="mt-0.5 block text-xs text-slate-600">Leave unchecked for multi-beam.</span>
                    </span>
                  </label>
                  {form.singleBeam ? (
                    <Input
                      label="Beam name *"
                      value={form.beamName}
                      onChange={(e) => updateField('beamName', e.target.value)}
                      error={fieldErrors.beamName}
                      autoComplete="off"
                      required
                    />
                  ) : null}
                </div>
              </div>

              <div className="border border-slate-200/80 rounded-xl overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-slate-900 tracking-tight">Feeders</h4>
                  <button
                    type="button"
                    onClick={addFeederRow}
                    className="w-9 h-9 rounded-full border border-slate-200 text-slate-700 hover:bg-white hover:border-primary-200 transition-colors"
                  >
                    +
                  </button>
                </div>
                <div className="p-3 overflow-x-auto">
                  <table className="w-full">
                    <tbody>
                      {form.feeders.map((row, index) => (
                        <tr key={row.id} className="border-b last:border-b-0">
                          <td className="py-2 pr-3 text-sm font-semibold text-slate-700 w-14">F{index + 1}</td>
                          <td className="py-2">
                            <SearchableSelect
                              value={row.feederId}
                              onChange={(id) => updateFeederRow(row.id, id)}
                              placeholder="Type to find feeder…"
                              emptyMessage="No feeder starts with that text"
                              className="py-2.5 text-sm"
                              options={feederOptions.map((item) => ({
                                value: String(item.id),
                                label: item.name || '',
                              }))}
                            />
                          </td>
                          <td className="py-2 pl-3 text-right w-16">
                            {form.feeders.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeFeederRow(row.id)}
                                className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {fieldErrors.feeders && <p className="px-4 pb-3 text-sm text-red-600">{fieldErrors.feeders}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Design *</label>
                <label className={`flex flex-col items-center justify-center px-4 py-8 border-2 border-dashed rounded-xl cursor-pointer ${
                  fieldErrors.designImage ? 'border-red-300 bg-red-50' : 'border-blue-300 bg-blue-50/20'
                }`}>
                  {form.designImage ? (
                    <AuthenticatedImage src={form.designImage} alt="Design preview" className="max-h-44 rounded-lg object-contain" />
                  ) : (
                    <div className="text-center text-gray-500">
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p>Add Design</p>
                    </div>
                  )}
                  <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={onFileChange} />
                </label>
                {uploadingImage && <p className="mt-1 text-sm text-gray-600">Uploading image...</p>}
                {fieldErrors.designImage && <p className="mt-1 text-sm text-red-600">{fieldErrors.designImage}</p>}
              </div>

          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDeleteItem)}
        title="Delete Design"
        message={confirmDeleteItem ? `Do you want to delete sample "${confirmDeleteItem.sampleNo}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteItem(null)}
        loading={deleteLoading}
        variant="danger"
      />

      <Modal
        open={showDesignPreview}
        onClose={closePreview}
        title="Design preview"
        size="2xl"
        bodyClassName="bg-slate-50/50"
        icon={(
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        )}
        footer={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canOrderDesign && (
              <Button type="button" onClick={openOrder}>
                Order this design
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={closePreview}>
              Close
            </Button>
          </div>
        )}
      >
        {previewDesign && (
          <div className="py-5 sm:py-6 grid grid-cols-1 lg:grid-cols-5 gap-5">
            <div className="lg:col-span-3 rounded-2xl border border-slate-200/80 bg-white shadow-sm min-h-[320px] flex items-center justify-center overflow-hidden">
              {previewDesign.designImage ? (
                <AuthenticatedImage
                  src={previewDesign.designImage}
                  alt={previewDesign.sampleNo || 'Design preview'}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-sm text-slate-500 py-16">No image available</div>
              )}
            </div>
            <div className="lg:col-span-2">
              <DetailSection title="Classification" columns={1}>
                <DetailField label="Sample number" value={previewDesign.sampleNo} mono />
                <DetailField label="Concept" value={previewDesign.conceptName} />
                <DetailField label="Category" value={previewDesign.categoryName} />
                <DetailField label="Sub category" value={previewDesign.subCategoryName} />
                <DetailField
                  label="Add picks"
                  value={previewDesign.addPicks != null ? String(previewDesign.addPicks) : '—'}
                  mono
                />
                <DetailField
                  label="Average picks"
                  value={previewDesign.averagePicks != null ? String(previewDesign.averagePicks) : '—'}
                  mono
                />
                <DetailField
                  label="Total cards"
                  value={previewDesign.totalCards != null ? String(previewDesign.totalCards) : '—'}
                  mono
                />
                <DetailField
                  label="Read count"
                  value={previewDesign.readCount != null ? String(previewDesign.readCount) : '—'}
                  mono
                />
                <DetailField
                  label="Note"
                  value={String(previewDesign.note || '').trim() || '—'}
                  span="full"
                />
                <DetailField
                  label="Type"
                  value={previewDesign.sampleDesign === true ? 'Sample' : 'Regular'}
                />
                <DetailField
                  label="Beam"
                  value={
                    previewDesign.singleBeam === true
                      ? `Single · ${String(previewDesign.beamName || '').trim() || '—'}`
                      : 'Multi-beam'
                  }
                />
                <DetailField
                  label="Feeders"
                  value={Array.isArray(previewDesign.feeders) ? String(previewDesign.feeders.length) : '0'}
                  mono
                />
              </DetailSection>
            </div>
          </div>
        )}
      </Modal>

      {showOrderOverlay && (
        <div className="fixed inset-0 z-[90] overflow-y-auto bg-slate-50">
          <div className="mx-auto flex min-h-0 w-full max-w-[1500px] flex-col px-3 py-3 sm:px-4 sm:py-4">
            <OrderDetail
              key={overlay.order === 'new' ? 'new' : overlay.order}
              orderId={overlay.order === 'new' ? null : overlay.order}
              initialSelectDesignId={overlay.preview}
              onBack={closeOrder}
              onDraftCreated={handleDraftCreated}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Designs;
